/* eslint-disable @typescript-eslint/no-explicit-any */

import * as React from 'react';
import { observer } from 'mobx-react';
import { observable, action, computed } from 'mobx';
import Select, { ActionMeta, components, InputActionMeta, MultiValue, SingleValue } from 'react-select';
import { FormFeedback } from 'reactstrap';

import { ApiUrls, KeyboardKeys } from '@app/AppConstants';

import { PersonLookupModel } from '@app/Models/WebApiModels';

import Extensions from '@app/Services/Extensions';
import ApiService from '@app/Services/ApiService';

import { BaseFormModel } from '../Services/BaseFormModel';

type SelectCallback = (value: string) => void;

interface IPersonFilterProps {
    className?: string;
    title: string;
    value: string;
    onSelect: SelectCallback;
    size?: string;
    name?: string;
    formModel?: BaseFormModel;
    isMulti?: boolean;
    isPersonDisabled?: (person: PersonLookupModel) => boolean;
}

export type IPersonFilter = {
    search?: string;
    searchByFullName?: boolean;
};

export enum SearchMatched {
    selected = 0,
    fullMatched = 1,
    searchMatched = 2
}

@observer
export default class PersonFilter extends React.Component<IPersonFilterProps, {}> {
    personFilterStore: PersonFilterStore;

    constructor(props: IPersonFilterProps) {
        super(props);
        const { formModel, name, isPersonDisabled, value } = props;
        this.personFilterStore = new PersonFilterStore(formModel || null, name || '', isPersonDisabled);
        this.personFilterStore.updateDefault(value);
    }

    componentDidUpdate(prevProps: IPersonFilterProps) {
        if(prevProps.value !== this.props.value){
            this.personFilterStore.updateDefault(this.props.value);
        }
    }

    render() {
        const { size, name, isMulti, title: titlte, onSelect, className } = this.props;
        let itemClass = 'filters-list-item';
        if (size === 'sm') {
            itemClass += ' size-sm';
        }
        if (!this.personFilterStore.isValidField ) {
            itemClass += ' is-invalid';
        }
        if (className){
            itemClass += ' ' + className;
        }
        let options = this.personFilterStore.options.slice();
        const MultiValueLabel = this.renderMultiValueLabel;
        return (
            <div className={itemClass}>
                <div className="filters-list-item-title">{titlte}</div>
                <div className="filters-list-item-field">
                    <Select
                        placeholder="None"
                        backspaceRemovesValue={true}
                        isLoading={this.personFilterStore.isLoadingExternally}
                        noOptionsMessage={() => this.personFilterStore.noResultsText}
                        loadingMessage={()=> 'loading'}
                        closeMenuOnSelect={!isMulti}
                        isMulti={isMulti}
                        hideSelectedOptions={false}
                        onChange={this.onChangeHandler}
                        onMenuClose={() => {
                            if (isMulti)
                                onSelect(this.personFilterStore.selectedPersons);
                            this.personFilterStore.isOpen = false;
                        }}
                        onMenuOpen={() => {
                            this.personFilterStore.loadPersons('', true);
                            this.personFilterStore.isOpen = true;
                        }}
                        onKeyDown={this.onKeyDownHandler}
                        onInputChange={this.onInputChangeHandler}
                        options={options}
                        getOptionLabel={option => option.text}
                        getOptionValue={option => option.code}
                        tabSelectsValue={true}
                        escapeClearsValue={true}
                        className="select-filter-container select-filter-single"
                        classNamePrefix="person select-filter"
                        isClearable={true}
                        value={this.filterOptionsForValue(options)}
                        isOptionDisabled={option => option.disabled}
                        inputValue={this.personFilterStore.inputvalue}
                        components={{MultiValueLabel}}
                    />
                    {!this.personFilterStore.isValidField && <FormFeedback>{this.personFilterStore.validatedErrors.map((error: string) => <div key={name + error}>{error}</div>)}</FormFeedback>}
                </div>
            </div>
        );
    }

    renderMultiValueLabel = (props: any) => {
        return <components.MultiValueLabel {...props}>{props.data.code}</components.MultiValueLabel>;
    };

    filterOptionsForValue(options: PersonSelectDTO[]){
        const personIds = this.personFilterStore.selectedPersons.split(',');
        return options.filter( option => personIds.includes(option.id));
    }

    onKeyDownHandler = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.keyCode === KeyboardKeys.COMMA) {
            if (!this.personFilterStore.isOpen) return;
            let searchValue = (event.target as HTMLInputElement).value;
            searchValue && this.personFilterStore.saveFromSearch(searchValue, this.props.isMulti || false);
            this.props.onSelect(this.personFilterStore.selectedPersons);
        } else if (event.keyCode === KeyboardKeys.BACKSPACE && !this.personFilterStore.isOpen) {
            const selected = this.personFilterStore.selectedPersons.split(',');
            if (selected && selected.length > 0) {
                selected.pop();
                this.personFilterStore.selectedPersons = selected.join(',');
                this.props.onSelect(this.personFilterStore.selectedPersons);
            }
        }
    };

    onInputChangeHandler = (input: string, meta: InputActionMeta) => {
        let modifiedInput = input.charAt(input.length -1 ) === ',' ? '' : input;
        this.personFilterStore.inputvalue = modifiedInput;
        this.personFilterStore && Extensions.executeTimeout(this.personFilterStore.loadPersons, 100, this.personFilterStore, modifiedInput);
    };

    onChangeHandler = (selectedPersons: SingleValue<PersonSelectDTO> | MultiValue<PersonSelectDTO>, meta: ActionMeta<PersonSelectDTO>) => {
        if(meta.action === 'clear'){
            this.personFilterStore.inputvalue = '';
            this.personFilterStore.selectedPersons = '';
            this.personFilterStore.loadAllPersonsForFilter();
        }

        if (this.personFilterStore.isEditByEnter) {
            this.personFilterStore.isEditByEnter = false;
            return;
        }

        if(selectedPersons){
            if (!this.props.isMulti) {
                let persons = selectedPersons as PersonSelectDTO;
                this.personFilterStore.selectedPersons = persons.id;
                this.props.onSelect(this.personFilterStore.selectedPersons);
                return;
            }

            let persons = selectedPersons as PersonSelectDTO[];
            this.personFilterStore.selectedPersons = persons.map(person => person.id).join(',');
        }

        if (!selectedPersons && !this.personFilterStore.isOpen) {
            this.personFilterStore.selectedPersons = '';
            this.props.onSelect(this.personFilterStore.selectedPersons);
        }
    };
}

class PersonFilterStore {
    @observable options: Array<PersonSelectDTO> = [];
    @observable isLoadingExternally: boolean = false;
    @observable selectedPersons: string = '';
    @observable inputvalue: string;
    isOpen: boolean = false;
    isEditByEnter: boolean = false;

    formModel: BaseFormModel | null;
    name: string;
    isPersonDisabled?: (person: PersonLookupModel) => boolean;

    private _isFirstLoad: boolean = true;

    constructor(formModel: BaseFormModel | null, name: string, isPersonDisabled?: (person: PersonLookupModel) => boolean) {
        this.formModel = formModel;
        this.name = name;
        this.isPersonDisabled = isPersonDisabled;
    }

    @action
    updateDefault(newValue?: string) {
        this.selectedPersons = typeof newValue === 'string' ? (newValue || '') : (newValue || '').toString();
        if (!newValue) {
            this.options = [];
        } else if (this._isFirstLoad || !this.options || this.options.length === 0) {
            this.loadSelectedPersons().then(data => {
                this.options = data as PersonSelectDTO[];
            });
        }
    }

    @action
    loadPersons(input: string, isOpen?: boolean) {
        if (this.options && this.options.length > 0 && !input && !isOpen) return;
        this.isLoadingExternally = true;
        let search = input ? input.trim() : null;

        this.loadAllPersonsForFilter(search).then(items => {
            this.isLoadingExternally = false;
            this.options = items as PersonSelectDTO[];
        });
    }

    @action
    loadSelectedPersons() {
        let data: IPersonFilter = {};
        return ApiService.getData(ApiUrls.PersonLookupUrl, data).then(response => {
            const data: PersonLookupModel[] = response.data;
            if (!data || data.length === 0) return Promise.resolve([]);
            const selectedGroup = data.map(item => {
                let name = '(' + item.code + ') ' + item.firstName + ' ' + item.lastName;
                const isDisabled = this.isPersonDisabled ? this.isPersonDisabled(item) : false;
                return new PersonSelectDTO(name, item.id, isDisabled, item.code);
            });
            return Promise.resolve(selectedGroup);
        });
    }

    @action
    loadAllPersonsForFilter(search?: string | null) {
        let sendData: IPersonFilter = {};
        if (search) {
            sendData.search = search;
            sendData.searchByFullName = true;
        }

        const lookUpPromise = ApiService.getData(ApiUrls.PersonLookupUrl, sendData).then(response => {
            const data: PersonLookupModel[] = response.data;
            if (!data || data.length === 0) return [];

            let persons: Array<PersonSelectDTO> = [];
            let fullMatched: Array<PersonSelectDTO> = [];
            let searchMatched: Array<PersonSelectDTO> = [];
            data.forEach(item => {
                if (this.selectedPersons.indexOf(item.id) > -1) return;
                let name = '(' + item.code + ') ' + item.firstName + ' ' + item.lastName;
                const isDisabled = this.isPersonDisabled ? this.isPersonDisabled(item) : false;
                if (item.searchState.toLowerCase() === SearchMatched[SearchMatched.fullMatched].toLowerCase()) {
                    fullMatched.push(new PersonSelectDTO(name, item.id, isDisabled, item.code));
                }
                else if (item.searchState.toLowerCase() === SearchMatched[SearchMatched.searchMatched].toLowerCase()) {
                    searchMatched.push(new PersonSelectDTO(name, item.id, isDisabled, item.code));
                }
            });

            if (fullMatched.length > 0) {
                if (this.selectedPersons || search)
                    persons.push(new PersonSelectDTO(`full matched (${fullMatched.length})`, 'fullMatched', true, 'fullMatched'));
                persons = persons.concat(fullMatched);
            }
            if (searchMatched.length > 0) {
                if (this.selectedPersons || search)
                    persons.push(new PersonSelectDTO(`search matched (${searchMatched.length})`, 'searchMatched', true, 'searchMatched'));
                persons = persons.concat(searchMatched);

            }
            return Promise.resolve(persons);
        });

        const lookUp = [lookUpPromise];
        if (this.selectedPersons) lookUp.push(this.loadSelectedPersons());
        return Promise.all(lookUp).then((values: any[]) => {
            let persons: Array<PersonSelectDTO> = [];
            if (values[1] && values[1].length > 0) {
                persons.push(new PersonSelectDTO(`selected (${values[1].length})`, 'selectedGroup', true, 'selectedGroup'));
                persons = persons.concat(values[1]);
            }
            persons = persons.concat(values[0]);
            return persons;
        });
    }

    @action
    saveFromSearch(searchValue: string, isMulti: boolean) {
        if (searchValue.replace(',', '').trim().length === 0) return;
        const persons = this.options;
        let selectedPersons = this.selectedPersons;
        const codes = searchValue.split(',').map((code: string) => code.trim().toUpperCase());
        let matchedList = persons.filter((item: PersonSelectDTO) => codes.indexOf(item.code.toUpperCase()) !== -1).map((item: PersonSelectDTO) => item.id);
        if (!matchedList || matchedList.length === 0)
            return;

        if (isMulti) {
            matchedList.forEach(id => {
                if (selectedPersons.indexOf(id) < 0) {
                    selectedPersons += selectedPersons.length > 0 ? ',' : '';
                    selectedPersons += id;
                }
            });
        } else {
            selectedPersons = matchedList.pop() || selectedPersons;
        }

        this.selectedPersons = selectedPersons;
        this.isEditByEnter = true;
    }

    @computed
    get noResultsText() {
        return this.isLoadingExternally ? 'Loading...' : 'No results found';
    }

    @computed
    get isValidField() {
        let valid = true;
        if (this.name && this.formModel) {
            valid = this.formModel.validated ? !this.formModel.invalidFields.includes(this.name) : !this.formModel.validated;
        }
        return valid;
    }

    @computed
    get validatedErrors() {
        let errors: string[] = [];
        if (this.name && this.formModel) {
            errors = this.formModel.errorFor(this.name);
        }
        return errors;
    }
}

class PersonSelectDTO {
    text: string;
    id: string;
    disabled: boolean;
    code: string;

    constructor(text: string, id: string, isDisable?: boolean, code?: string) {
        this.text = text;
        this.id = id;
        this.disabled = isDisable !== undefined ? isDisable : false;
        this.code = code ? code : '';
    }
}